import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { BookOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { addLibraryTopics, createTopic, deleteTopic, getTopics, updateTopic } from "./api";

const LibraryModal = ({ open, library, onClose, onAdded }) => {
  const { t } = useTranslation("dashboard");
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const available = (library || []).filter(item => !item.added);

  useEffect(() => {
    if (open) setKeys(available.map(item => item.key));
  }, [open]);

  const add = async () => {
    setBusy(true);
    try {
      const res = await addLibraryTopics(keys);
      Success(t("{{n}} topics added", { n: res.data.added }));
      onAdded();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="safety-page"
      open={open}
      width={720}
      title={t("Topics for trade businesses")}
      okText={t("Add")}
      cancelText={t("Cancel")}
      okButtonProps={{ disabled: !keys.length }}
      confirmLoading={busy}
      onOk={add}
      onCancel={onClose}
    >
      <Typography.Paragraph type="secondary">
        {t(
          "Each topic is repeated yearly for all employees. You can change the interval and who it applies to afterwards."
        )}
      </Typography.Paragraph>
      <Checkbox.Group className="sf-full" value={keys} onChange={setKeys}>
        <List
          dataSource={available}
          locale={{ emptyText: t("All topics have been added.") }}
          renderItem={item => (
            <List.Item>
              <Checkbox value={item.key}>
                <Typography.Text strong>{item.title}</Typography.Text>
                <div className="sf-small">{item.legalBasis}</div>
              </Checkbox>
            </List.Item>
          )}
        />
      </Checkbox.Group>
    </Modal>
  );
};

const TopicModal = ({ open, topic, employees, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const appliesTo = Form.useWatch("appliesTo", form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      topic?._id
        ? { ...topic, employees: (topic.employees || []).map(String) }
        : { intervalMonths: 12, appliesTo: "all", employees: [], active: true }
    );
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      if (topic?._id) await updateTopic(topic._id, values);
      else await createTopic(values);
      Success(t("Saved"));
      onSaved();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="safety-page"
      open={open}
      width={720}
      title={topic?._id ? t("Edit topic") : t("New topic")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="title" label={t("Title")} rules={[{ required: true }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="legalBasis" label={t("Legal basis / rules")}>
          <Input maxLength={300} />
        </Form.Item>
        <Form.Item name="content" label={t("Content")}>
          <Input.TextArea autoSize={{ minRows: 4, maxRows: 14 }} />
        </Form.Item>
        <Row gutter={[12, 0]}>
          <Col xs={12} md={8}>
            <Form.Item
              name="intervalMonths"
              label={t("Repeat every (months)")}
              rules={[{ required: true }]}
            >
              <InputNumber className="sf-full" min={1} max={60} precision={0} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="appliesTo" label={t("Applies to")}>
              <Select
                options={[
                  { value: "all", label: t("All employees") },
                  { value: "selected", label: t("Selected employees") }
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="active" label={t("Active")} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        {appliesTo === "selected" && (
          <Form.Item name="employees" label={t("Employees")}>
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={(employees || []).map(e => ({ value: e._id, label: e.name }))}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

/** The company's Unterweisung topics: from the library or own ones, interval and scope. */
const Topics = ({ meta, onChanged }) => {
  const { t } = useTranslation("dashboard");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getTopics();
      setData(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changed = () => {
    load();
    onChanged();
  };

  const remove = async id => {
    try {
      const res = await deleteTopic(id);
      Success(
        res.data.deactivated ? t("The topic was taught before and is now inactive.") : t("Deleted")
      );
      changed();
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  return (
    <>
      <PageToolbar
        actions={
          meta?.can.create && (
            <Space wrap>
              <Button icon={<BookOutlined />} onClick={() => setLibraryOpen(true)}>
                {t("Add from library")}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
                {t("New topic")}
              </Button>
            </Space>
          )
        }
      />
      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        dataSource={data}
        mobileAvatar={false}
        locale={{ emptyText: t("No topics yet") }}
        pagination={false}
        columns={[
          {
            title: t("Topic"),
            key: "title",
            mobile: "title",
            render: (_, item) => (
              <div>
                <Typography.Text strong className="sf-wrap">
                  {item.title}
                </Typography.Text>
                <div className="sf-small sf-wrap">{item.legalBasis}</div>
              </div>
            )
          },
          {
            title: t("Interval"),
            key: "interval",
            mobile: "subtitle",
            render: (_, item) => t("every {{n}} months", { n: item.intervalMonths })
          },
          {
            title: t("Applies to"),
            key: "scope",
            render: (_, item) =>
              item.appliesTo === "all"
                ? t("All employees")
                : t("{{n}} employees", { n: (item.employees || []).length })
          },
          {
            title: t("Status"),
            key: "active",
            mobile: "badge",
            render: (_, item) =>
              item.active ? <Tag color="success">{t("Active")}</Tag> : <Tag>{t("Inactive")}</Tag>
          },
          {
            title: "",
            key: "actions",
            mobile: "actions",
            render: (_, item) => (
              <Space>
                {meta?.can.update && (
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    aria-label={t("Edit")}
                    onClick={() => setEditing(item)}
                  />
                )}
                {meta?.can.delete && (
                  <Popconfirm title={t("Delete this topic?")} onConfirm={() => remove(item._id)}>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={t("Delete")}
                    />
                  </Popconfirm>
                )}
              </Space>
            )
          }
        ]}
      />
      <LibraryModal
        open={libraryOpen}
        library={meta?.library}
        onClose={() => setLibraryOpen(false)}
        onAdded={() => {
          setLibraryOpen(false);
          changed();
        }}
      />
      <TopicModal
        open={Boolean(editing)}
        topic={editing}
        employees={meta?.employees}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          changed();
        }}
      />
    </>
  );
};

export default Topics;
